// User and Authentication Types
export interface User {
  id: string;
  username: string;
  role: 'corporate' | 'regional' | 'district' | 'dealer' | 'admin';
  distributor: string;
  is_corporate: boolean;
  is_regional: boolean;
  is_district: boolean;
  is_dealer: boolean;
  is_admin?: boolean;  // NEW: For corporate users with admin privileges
  manager_name?: string;
  reports_to?: string;
  manages?: string[];

  // Legacy fields for backward compatibility
  distributor_id?: number;
  dealer_id?: string;  // JWT dealer_id field (the string like "78903549")
  dealer_name?: string;  // Full dealer name from MongoDB dealer_locations (e.g., "ELLINGTON AGWAY PWR SVC DIV")
  name?: string;
  email?: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  dma_code?: number;
}

export interface LoginResponse {
  success: boolean;
  user?: User;
  redirect?: string;
  error?: string;
}

export interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

// Distributor Types
export interface Distributor {
  distributor_id: number;
  name: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  dma_code: number;
  email: string;
}

// Report Types
export interface ChannelMetrics {
  impressions: number;
  clicks: number;
}

export interface SummaryMetrics {
  display: number | ChannelMetrics;
  dooh: number | ChannelMetrics;
  streaming_tv: number | ChannelMetrics;
  streaming_audio: number | ChannelMetrics;
  broadcast_tv: number | ChannelMetrics;
  radio: number | ChannelMetrics;
  sport_signage: number | ChannelMetrics;
  youtube: number | ChannelMetrics;
  social: number | ChannelMetrics;
  paid_social: number | ChannelMetrics;
  store_visits: number | ChannelMetrics;
}

// Sport Signage event data — one row per team/DMA combination over a date range
export interface SportSignageData {
  start_date: string;
  end_date: string;
  time_et: string;
  away_team: string;
  home_team: string;
  conference: string;
  tv_network: string;
  impressions: number;
  home_dma_name: string;
  away_dma_name: string;
}

export interface ChartDataPoint {
  date: string;
  impressions: number;
  clicks?: number;
}

export interface RadioData {
  date: string;
  station: string;
  impressions: number;
  dma: string;
  dma_code: number;
}

// NEW: Television data interface
export interface TelevisionData {
  date: string;
  network: string;
  program: string;
  demo: string;
  impressions: number;
  zone: string;
}

export interface MapDataPoint {
  zip: string;
  impressions: number;
}

export interface MapData {
  display: MapDataPoint[];
  dooh: MapDataPoint[];
  streaming_tv: MapDataPoint[];
}

export interface AllReportData {
  summary: SummaryMetrics;
  display_chart: ChartDataPoint[];
  dooh_chart: ChartDataPoint[];
  streaming_tv_chart: ChartDataPoint[];
  streaming_audio_chart: ChartDataPoint[];
  broadcast_tv_chart: ChartDataPoint[];
  sport_signage_chart: ChartDataPoint[];
  radio_chart: ChartDataPoint[];
  youtube_chart: ChartDataPoint[];
  search_chart: ChartDataPoint[];
  social_chart: ChartDataPoint[];
  paid_social_chart: ChartDataPoint[];
  store_visits_chart: ChartDataPoint[];
  radio: RadioData[];
  television: TelevisionData[];
  sport_signage: SportSignageData[];
  map: MapData;
}

// Date Range Type
export interface DateRange {
  startDate: Date;
  endDate: Date;
}

// API Response Types
export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

// Channel Types
export type AdvertisingChannel = 'display' | 'dooh' | 'streaming_tv' | 'streaming_audio' | 'broadcast_tv' | 'sport_signage' | 'youtube' | 'search' | 'radio' | 'social' | 'paid_social' | 'store_visits';

// Color Map for Channels
export const CHANNEL_COLORS: Record<AdvertisingChannel, string> = {
  display: '#FF6B6B',           // Coral red
  dooh: '#4ECDC4',              // Teal
  streaming_tv: '#45B7D1',      // Sky blue
  streaming_audio: '#F97316',   // Orange — Streaming Audio (Spotify/Pandora)
  broadcast_tv: '#FFA500',      // Amber — Traditional television
  sport_signage: '#2ECC71',     // Green — Sport Signage
  youtube: '#FF0000',           // YouTube red
  search: '#4285F4',            // Google blue
  radio: '#FFD93D',             // Yellow — Broadcast Radio
  social: '#1877F2',            // Facebook blue
  paid_social: '#1DA1F2',       // Twitter/Social blue
  store_visits: '#9B59B6'       // Purple
};

// Channel Display Names
export const CHANNEL_NAMES: Record<AdvertisingChannel, string> = {
  display: 'Display',
  dooh: 'DOOH',
  streaming_tv: 'Streaming TV',
  streaming_audio: 'Streaming Audio',
  broadcast_tv: 'Broadcast TV',
  sport_signage: 'Sport Signage',
  youtube: 'YouTube',
  search: 'Search',
  radio: 'Radio',
  social: 'Social',
  paid_social: 'Paid Social',
  store_visits: 'Store Visits'
};
